import React from "react";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import { motion } from "framer-motion";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import "./ProgramsBanner.css";
import { useDonation } from "../Components/DonationContext";
import { useNavigate } from "react-router-dom";
import "./EconomicDevelopment.css";

// Animation variants for Framer Motion
const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3,
    },
  },
};

const staggerItem = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

/* ---- Simple inline icons (no external icon-font dependency) ---- */
const IconLeaf = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M5 21c8 0 14-6 14-14V5h-2C9 5 3 11 3 19v2h2z" />
    <path d="M5 21c4-6 8-9 14-12" />
  </svg>
);

const IconWheelchair = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#1f9d55" strokeWidth="1.8">
    <circle cx="9" cy="5" r="1.6" />
    <path d="M9 8v5l4 4" />
    <path d="M9 13H5" />
    <path d="M13 8l2 4h4" />
    <circle cx="9" cy="16" r="4.5" />
  </svg>
);

const IconCap = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#1f9d55" strokeWidth="1.8">
    <path d="M2 9l10-4 10 4-10 4-10-4z" />
    <path d="M6 11v4c0 1.7 2.7 3 6 3s6-1.3 6-3v-4" />
    <path d="M22 9v6" />
  </svg>
);

const IconGroup = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#1f9d55" strokeWidth="1.8">
    <circle cx="9" cy="8" r="3" />
    <path d="M2.5 20c0-3.5 2.9-6 6.5-6s6.5 2.5 6.5 6" />
    <circle cx="17.5" cy="8.5" r="2.3" />
    <path d="M15.7 14c2.6.4 4.7 2.5 4.7 5.5" />
  </svg>
);

const IconBook = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#1f9d55" strokeWidth="1.8">
    <path d="M4 5.5C4 4.7 4.7 4 5.5 4H12v16H5.5A1.5 1.5 0 014 18.5v-13z" />
    <path d="M20 5.5c0-.8-.7-1.5-1.5-1.5H12v16h6.5a1.5 1.5 0 001.5-1.5v-13z" />
  </svg>
);

const IconBank = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#1f9d55" strokeWidth="1.8">
    <path d="M3 10l9-6 9 6" />
    <path d="M4 10h16v2H4z" />
    <path d="M5 12v7M9 12v7M15 12v7M19 12v7" />
    <path d="M3 21h18" />
  </svg>
);

const IconHeartFilled = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" stroke="none">
    <path d="M12 21s-7.5-4.6-10-9.3C.4 8.6 2 5 5.6 5c2 0 3.4 1.1 4.4 2.6C11 6.1 12.4 5 14.4 5 18 5 19.6 8.6 22 11.7 19.5 16.4 12 21 12 21z" />
  </svg>
);

const IconUserPlus = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="9" cy="8" r="3.2" />
    <path d="M2.5 20c0-3.6 2.9-6.2 6.5-6.2s6.5 2.6 6.5 6.2" />
    <path d="M18 8v5M20.5 10.5h-5" />
  </svg>
);

// Renamed component
const EconomicDevelopment = () => {
  // Data for the initiatives section for cleaner code
  const initiatives = [
    {
      icon: <IconWheelchair />,
      title: "Providing Mobility Aids & Assistive Devices",
      text: "Enhancing accessibility and independence for a better life.",
    },
    {
      icon: <IconCap />,
      title: "Vocational Training & Entrepreneurship Programs",
      text: "Building skills and creating sustainable livelihood opportunities.",
    },
    {
      icon: <IconBook />,
      title: "Inclusion in Mainstream Schools & Training",
      text: "Promoting inclusive education and equal opportunities.",
    },
    {
      icon: <IconGroup />,
      title: "Formation of Self-Help Groups (SHGs)",
      text: "Empowering communities through collective strength and savings.",
    },
    {
      icon: <IconBank />,
      title: "Facilitating Credit through Financial Institutions",
      text: "Providing financial access to turn aspirations into reality.",
    },
  ];

  const highlights = [
    { icon: <IconWheelchair />, text: "Mobility Aids & Assistive Devices" },
    { icon: <IconCap />, text: "Vocational Training & Entrepreneurship" },
    { icon: <IconGroup />, text: "Self Help Groups & Credit Support" },
  ];

  const { openDonation } = useDonation();
  const navigate = useNavigate();

  return (
    <>
      <Header />

      {/* Hero Section */}
      <section className="page-header page-header--ed">
        <div
          className="page-header__bg"
          style={{
            backgroundImage: "url(/assets/images/backgrounds/Economic-Development.jpg)",
          }}
        ></div>
        <div className="shape1 float-bob-x">
          <img src="assets/images/shapes/page-header-shape1.png" alt="" />
        </div>
        <div className="shape2">
          <img src="assets/images/shapes/page-header-shape2.png" alt="" />
        </div>
        <Container>
          <div className="page-header__inner text-center">
            <motion.h2
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Economic Development
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              style={{ color: "#dad2d0", fontSize: "22px", marginTop: "20px" }}
            >
              Vikash drives economic development by providing training,
              resources, and opportunities to enhance livelihoods and promote
              sustainable growth.
            </motion.p>
          </div>
        </Container>
      </section>

      {/* Introduction Section */}
      <section className="py-5 ed-mission-section">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Row className="align-items-center justify-content-center g-5">
              <motion.div className="col-lg-6" variants={staggerItem}>
                <span className="ed-eyebrow">
                  <IconLeaf /> Our Mission
                </span>
                <h2 className="fw-bold mb-2" style={{ color: "#006633" }}>
                  Fostering Sustainable Independence
                </h2>
                <div className="ed-heading-underline">
                  <IconLeaf />
                </div>
                <p className="lh-lg">
                  Our assistance includes mobility aids, assistive devices,
                  and inclusion in regular schools. Vikash undertakes
                  vocational training and Entrepreneur Development Programs
                  for the disabled to enhance their income. Since
                  independence is easier to achieve in an empathetic group,
                  we organize Self Help Groups (SHGs) and arrange credit
                  through financial institutions.
                </p>
              </motion.div>
              <motion.div className="col-lg-6" variants={staggerItem}>
                <div className="ed-image-card">
                  <img
                    src="/assets/images/Subsections/ede1.jpg"
                    alt="Vocational training session"
                    className="img-fluid rounded-5 shadow"
                  />
                  <div className="ed-image-strip">
                    {highlights.map((h, idx) => (
                      <div className="ed-image-strip__item" key={idx}>
                        <span className="ed-image-strip__icon">{h.icon}</span>
                        <span>{h.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* CBR Model Section */}
      <section className="py-5 ed-cbr-section">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <div className="ed-cbr-box">
              <Row className="align-items-center g-5">
                <motion.div className="col-lg-6" variants={staggerItem}>
                  <div className="ed-cbr-icon">
                    <IconGroup />
                  </div>
                  <span className="ed-eyebrow">
                    <IconLeaf /> Our Approach
                  </span>
                  <h2 className="fw-bold mb-2" style={{ color: "#006633" }}>
                    Our Community-Based Approach
                  </h2>
                  <div className="ed-heading-underline">
                    <IconLeaf />
                  </div>
                  <p className="lh-lg mb-0">
                    Disability affects a wide range of socio-economic
                    outcomes. The Vikash model of Community Based
                    Rehabilitation (CBR) includes all possible ways to assist
                    people with disabilities to live more independently, with
                    their families, and in their communities.
                  </p>
                </motion.div>
                <motion.div className="col-lg-6" variants={staggerItem}>
                  <img
                    src="/assets/images/Subsections/ede2.jpg"
                    alt="Community Based Rehabilitation model in action"
                    className="img-fluid rounded-5 shadow"
                  />
                </motion.div>
              </Row>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* Key Programs Section */}
      <section className="py-5 ed-programs-section">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
            className="text-center mb-5"
          >
            <span className="ed-eyebrow ed-eyebrow--center">
              <IconLeaf /> Our Key Economic Support Programs
            </span>
            <h2 className="fw-bold" style={{ color: "#16341f" }}>
              Our Key Economic Support Programs
            </h2>
          </motion.div>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Row className="g-4 justify-content-center">
              {initiatives.map((initiative, index) => (
                <Col md={6} lg={4} key={index}>
                  <motion.div variants={staggerItem} className="h-100">
                    <Card className="p-4 border-0 shadow-sm h-100 ed-program-card">
                      <Card.Body className="d-flex align-items-start gap-3 p-0">
                        <span className="ed-program-icon">{initiative.icon}</span>
                        <div>
                          <h6 className="mb-1">{initiative.title}</h6>
                          <p className="mb-0">{initiative.text}</p>
                        </div>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Call To Action Section */}
      <section className="py-5 ed-cta-section">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
          >
            <div className="ed-cta-box">
              <div className="ed-cta-content">
                <div className="ed-cta-icon">
                  <IconHeartFilled />
                </div>
                <span className="ed-eyebrow ed-eyebrow--hero">
                  Make An Impact
                </span>
                <h2 className="fw-bold mb-2 text-white">Empower a Livelihood</h2>
                <div className="ed-heading-underline ed-heading-underline--hero">
                  <IconLeaf />
                </div>
                <p className="mb-4">
                  Your contribution supports vocational training and
                  resources that help people with disabilities achieve
                  economic independence.
                </p>
                <div className="ed-cta-buttons">
                  <Button className="ed-btn ed-btn--solid" onClick={openDonation}>
                    <IconHeartFilled /> Donate
                  </Button>
                  <Button
                    className="ed-btn ed-btn--outline"
                    onClick={() => navigate("/interns")}
                  >
                    <IconUserPlus /> Join Us
                  </Button>
                </div>
              </div>
              <div className="ed-cta-image">
                <img
                  src="/assets/images/backgrounds/do1.png"
                  alt="Growing together"
                />
              </div>
            </div>
          </motion.div>
        </Container>
      </section>

      <Footer />

      <a href="#" data-target="html" className="scroll-to-target scroll-to-top">
        <span className="scroll-to-top__wrapper">
          <span className="scroll-to-top__inner" />
        </span>
        <span className="scroll-to-top__text"> Go Back Top</span>
      </a>
    </>
  );
};

export default EconomicDevelopment;